import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { GlobalVariables, config } from '../config/configurations';
import { getCookie } from '../helpers/cookies';
import { forceLogout } from './authentication';
import { refreshMS, renewFromTeamsSso } from '@common/pages/login/ms-services';
import { store } from '@store/index';
import { logout } from '../components/authentication/auth.thunks';
import { cleanLocalStorage } from '@common/helpers/localStorage';

const isTeamsEnvironment = () => {
  return localStorage.getItem('integrationMode') === 'teams';
};

const isOnConfigPages = () => {
  const isTeams = isTeamsEnvironment();
  return (
    isTeams && (window.location.pathname === '/overview' || window.location.pathname === '/config')
  );
};

const isOnLoginPage = () => {
  const globalVar = GlobalVariables.getInstance();
  return (
    (globalVar.integrationMode !== 'addon' && window.location.pathname === '/login') ||
    (globalVar.integrationMode === 'addon' && window.location.pathname === '/index.html#/login')
  );
};

const createAxiosInstance = () => {
  return axios.create({
    withCredentials: true,
    baseURL: config.REACT_APP_URL_BACKEND_API,
    headers: {
      'Access-Control-Allow-Headers':
        'Origin, Content-Type, x-access-token, X-Headers-Authorization',
      appId: config.REACT_APP_ID,
      version: config.REACT_APP_API_VERSION,
      client: config.REACT_APP_CLIENT,
      'X-Headers-Authorization': getCookie('x-access-token')
        ? 'x-cors-context'
        : 'x-same-site-context',
      Authorization: getCookie('x-access-token') ? `Bearer ${getCookie('x-access-token')}` : null,
    },
  });
};

const instance = createAxiosInstance();

export function reinitializeInstance() {
  // Preserve the configured interceptors. Replacing the Axios instance here
  // races the first protected request and silently drops refresh handling.
  updateAuthHeaders();
}

export const configureAxios = (isTeams: boolean) => {
  instance.defaults.headers.common['x-web-integration'] = isTeams ? 'teams' : 'web';
};

const refreshFromSession = async () => {
  const globalVar = GlobalVariables.getInstance();
  const response = await instance.post(
    `${config.REACT_APP_URL_BACKEND_API.replace(/\/$/, '')}/${config.REACT_APP_API_VERSION}/authentication/oauth2/refresh`,
    {
      grant_type: 'refresh_token',
      instance: globalVar.getIntegrationMode(),
    },
  );
  return response.data;
};

const renewSession = async () => {
  try {
    return await refreshFromSession();
  } catch (error: any) {
    // The refresh token lives in the session cookie of the API, which a
    // Teams webview may drop. The Teams identity then renews the access
    // token; the refresh token stays on the server either way.
    if (error?.response?.status !== 401 || !isTeamsEnvironment()) {
      throw error;
    }
    try {
      return await renewFromTeamsSso();
    } catch {
      // No identity to renew from either: the session is over, and the
      // 401 is what tells the caller so.
      throw error;
    }
  }
};

let renewal: Promise<any> | null = null;

export const refreshAppToken = async () => {
  // A burst of 401s shares one renewal. A Teams grant rotates the user's
  // refresh token, so concurrent grants would revoke each other.
  if (!renewal) {
    renewal = renewSession().finally(() => {
      renewal = null;
    });
  }
  const data = await renewal;
  updateAuthHeaders();
  return data;
};

/**
 * The failed request captured its headers when it was sent. A renewal that
 * could not set the API cookies leaves the new token in the instance
 * defaults only, so the retry takes its credentials from there.
 */
export const withRenewedAuthHeaders = (requestConfig: InternalAxiosRequestConfig) => {
  const headers = new AxiosHeaders(requestConfig.headers);
  for (const name of ['Authorization', 'X-Headers-Authorization']) {
    const value = instance.defaults.headers.common[name];
    if (typeof value === 'string') {
      headers.set(name, value);
    }
  }
  return { ...requestConfig, headers };
};

const handleTokenRefresh = async (error: any) => {
  try {
    await refreshAppToken();
    return axios(withRenewedAuthHeaders(error.config));
  } catch (refreshError: any) {
    if (refreshError?.response?.status === 401) {
      await handleLogout();
    }
    throw refreshError;
  }
};

const handleMSRefresh = async (error: any) => {
  try {
    await refreshMS();
    return axios(error.config);
  } catch (refreshError) {
    if (isTeamsEnvironment()) {
      window.location.href = '/overview';
    } else {
      await handleLogout();
    }
    throw refreshError;
  }
};

const handleLogout = async () => {
  try {
    await store.dispatch(logout()).unwrap();
    cleanLocalStorage();
  } catch (logoutError) {
    await forceLogout();
  }
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isRefreshOrLogoutRequest =
      error.request?.responseURL?.includes('oauth2/refresh') ||
      error.request?.responseURL?.includes('oauth2/logout') ||
      error.request?.responseURL?.includes('oauth2/grant') ||
      error.request?.responseURL?.includes('oauth2/authorize');

    if (isRefreshOrLogoutRequest) {
      return Promise.reject(error);
    }

    if (isOnConfigPages()) {
      return handleMSRefresh(error);
    }

    if (error?.response?.status === 401 && !isOnLoginPage()) {
      return handleTokenRefresh(error);
    }

    return Promise.reject(error);
  },
);

export const updateAuthHeaders = () => {
  const token = getCookie('x-access-token');
  const isTeams = isTeamsEnvironment();

  instance.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : null;
  instance.defaults.headers.common['X-Headers-Authorization'] = token
    ? 'x-cors-context'
    : 'x-same-site-context';
  instance.defaults.headers.common['x-web-integration'] = isTeams ? 'teams' : 'web';
};

export const getAxiosInstance = () => instance;

export { instance };
